// Catalog driving both Standardmandat tabs. `integrationKey` matches the backend IntegrationName so
// the existing global SKR mappings (state.commons.standardAccountMappings, keyed by IntegrationName)
// can supply suggestions. Each "line" maps 1:1 to a backend row
// (fieldName -> {accountValue, buCode, startDate}); the store is field-name agnostic.

export const STANDARDMANDAT_TABS = {
    ERLOESE: "erloese",
    ZAHLUNGEN: "zahlungen",
};

// ---- Tab "Erlöse & Debitoren" -------------------------------------------------------------------

export const ERLOESE_SECTIONS = {
    UST_STANDARD: "USt-Standard",
    STEUERFREI: "Steuerfreie Umsätze",
    GUTSCHEINE: "Gutscheine",
    DEBITOREN: "Debitoren",
};

// Shared revenue/debtor form for every shop / marketplace integration on the Erlöse tab.
// `mappingName` links a line to its CommonAccountName identifier so the existing global SKR mapping
// (state.commons.standardAccountMappings) can supply SKR03/SKR04 account suggestions. Note the
// backend only ships sales-account suggestions for Shopify & WooCommerce today.
export const ERLOESE_LINES = [
    { fieldName: "revenueReducedDe", mappingName: "defaultSalesAccount7", label: "7 % / 5 % Umsatzsteuer – DE", section: ERLOESE_SECTIONS.UST_STANDARD, hasBu: true },
    { fieldName: "revenueStandardDe", mappingName: "defaultSalesAccount19", label: "19 % / 16 % Umsatzsteuer – DE", section: ERLOESE_SECTIONS.UST_STANDARD, hasBu: true },
    { fieldName: "revenueReducedEu", mappingName: "defaultSalesAccountEU7", label: "7 % / 5 % Umsatzsteuer – EU", subtitle: "OSS-Länder", section: ERLOESE_SECTIONS.UST_STANDARD, hasBu: true },
    { fieldName: "revenueStandardEu", mappingName: "defaultSalesAccountEU19", label: "19 % / 16 % Umsatzsteuer – EU", subtitle: "OSS-Länder", section: ERLOESE_SECTIONS.UST_STANDARD, hasBu: true },

    { fieldName: "reverseCharge", mappingName: "defaultSalesAccountEUExempt", label: "Steuerfrei EU (Reverse Charge)", subtitle: "B2B-Lieferungen ins EU-Ausland", section: ERLOESE_SECTIONS.STEUERFREI, hasBu: true },
    { fieldName: "oss", mappingName: "defaultSalesAccountEUMoss", label: "OSS (mit Startdatum)", subtitle: "One-Stop-Shop-Meldung", section: ERLOESE_SECTIONS.STEUERFREI, hasBu: true, hasStartDate: true },
    { fieldName: "thirdCountry", mappingName: "defaultSalesAccountExempt", label: "Steuerfrei Drittland", subtitle: "Exporte außerhalb der EU", section: ERLOESE_SECTIONS.STEUERFREI, hasBu: true },

    { fieldName: "voucherLiability", mappingName: "giftCardSalesAccount", label: "Gutscheine (Verbindlichkeit)", section: ERLOESE_SECTIONS.GUTSCHEINE },
    { fieldName: "voucherExpired", mappingName: "expiredGiftCardSalesAccount", label: "Abgelaufene Gutscheine", section: ERLOESE_SECTIONS.GUTSCHEINE },
    { fieldName: "voucherDiscounts", mappingName: "discountGiftCardSalesAccount", label: "Gutschein Rabatte / Discounts", section: ERLOESE_SECTIONS.GUTSCHEINE },

    { fieldName: "debtorAccount", label: "Standard-Konto für Debitoren", section: ERLOESE_SECTIONS.DEBITOREN, mandatory: true, isDebitor: true },
];

export const ERLOESE_INTEGRATIONS = [
    { key: "SHOPIFY", name: "Shopify" },
    { key: "AMAZON", name: "Amazon" },
    { key: "EBAY", name: "eBay" },
    { key: "OTTO", name: "Otto" },
    { key: "ABOUT_YOU", name: "AboutYou" },
    { key: "WOO_COMMERCE", name: "WooCommerce" },
    { key: "KAUFLAND", name: "Kaufland" },
    { key: "ZALANDO", name: "Zalando" },
    { key: "TIKTOK_SHOP", name: "TikTok Shop" },
];

// ---- Tab "Zahlungen" ----------------------------------------------------------------------------

export const ZAHLUNG_SECTIONS = {
    BUCHUNGSKONTEN: "Buchungskonten",
    MARKTPLATZGEBUEHREN: "Marktplatzgebühren",
};

// Standard payment "Buchungskonten" rows shared by every provider. bookingAccount
// (Sammeldebitorkonto) is read-only — it is sourced from the system's debtor mapping.
const PAYMENT_BASE_LINES = [
    { fieldName: "bookingAccount", label: "Sammeldebitorkonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN, readOnly: true, mandatory: true, isDebitor: true },
    { fieldName: "clearingAccount", label: "Transitkonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN, mandatory: true },
    { fieldName: "receivableAccount", label: "Bank-/Guthabenkonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN, mandatory: true },
    { fieldName: "costAccount", label: "Gebührenkonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN, hasBu: true, mandatory: true },
    { fieldName: "holdingAccount", label: "Freigabe-/Einbehaltekonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN },
    { fieldName: "otherAccount", label: "Klärenkonto", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN },
];

// Provider-specific extra rows appended to the base set.
const PAYMENT_EXTRA_LINES = {
    SHOPIFY_POS: [
        { fieldName: "cashDifferenceAccount", label: "Konto für Kassendifferenzen", section: ZAHLUNG_SECTIONS.BUCHUNGSKONTEN },
    ],
    KAUFLAND_PAYMENTS: [
        { fieldName: "marketplaceFeeAccount", label: "Marktplatzgebühren", section: ZAHLUNG_SECTIONS.MARKTPLATZGEBUEHREN, hasBu: true },
    ],
};

// Payment providers grouped into the handoff's three sub-sections. `key` matches the backend
// IntegrationName so payment-account suggestions resolve via the global SKR mapping.
export const ZAHLUNG_GROUPS = [
    {
        title: "Zahlungsdienstleister",
        providers: [
            { key: "PAYPAL", name: "PayPal" },
            { key: "STRIPE_PAYMENTS", name: "Stripe" },
            { key: "MOLLIE", name: "Mollie" },
            { key: "ADYEN", name: "Adyen" },
            { key: "KLARNA", name: "Klarna" },
            { key: "BRAINTREE", name: "Braintree" },
            { key: "GOCARDLESS", name: "GoCardless" },
            { key: "UNZER", name: "Unzer" },
        ],
    },
    {
        title: "Shop-integrierte Zahlungen",
        providers: [
            { key: "SHOPIFY_PAYMENTS", name: "Shopify Payments" },
            { key: "SHOPIFY_POS", name: "Shopify POS" },
            { key: "TIKTOK_PAYMENTS", name: "TikTok Payments" },
        ],
    },
    {
        title: "Marktplatz-Auszahlungen",
        providers: [
            { key: "AMAZON_PAYOUT", name: "Amazon Payout" },
            { key: "ABOUT_YOU_PAYMENTS", name: "AboutYou Payment" },
            { key: "OTTO_PAYMENTS", name: "Otto Payments" },
            { key: "EBAY_PAYMENTS", name: "eBay Payments" },
            { key: "KAUFLAND_PAYMENTS", name: "Kaufland Payments" },
            { key: "ZALANDO_PAYMENTS", name: "Zalando Payments" },
        ],
    },
];

// Flat list of every payment provider (used for iteration, defaults, and save).
export const ZAHLUNG_PROVIDERS = ZAHLUNG_GROUPS.flatMap((group) => group.providers);

/** Field lines for one integration, per tab. */
export const getLinesForIntegration = (tab, integrationKey) => {
    if (tab === STANDARDMANDAT_TABS.ERLOESE) {
        return ERLOESE_LINES;
    }
    return [...PAYMENT_BASE_LINES, ...(PAYMENT_EXTRA_LINES[integrationKey] || [])];
};

/** Column headers per tab: Erlöse uses Typ/Erlöskonto/BU-Schlüssel, Zahlungen uses Name/Konto/BU. */
export const getColumnHeaders = (tab) =>
    tab === STANDARDMANDAT_TABS.ERLOESE
        ? { type: "Typ", account: "Erlöskonto", bu: "BU-Schlüssel" }
        : { type: "Name", account: "Konto", bu: "BU" };

/** Distinct sections (in order) for the given tab's line set. */
export const getSectionsForLines = (lines) => {
    return lines.reduce((sections, line) => {
        if (!sections.includes(line.section)) {
            sections.push(line.section);
        }
        return sections;
    }, []);
};
